/**
 * The single canonical coordinate-conversion pipeline.
 *
 *   MediaPipe normalized image coordinates
 *     -> rotation-corrected frame coordinates   (device/sensor orientation)
 *     -> displayed video coordinates            (object-fit crop/letterbox + mirroring)
 *     -> normalized screen coordinates          (isotropic, origin centre, +y up)
 *     -> theremin interaction coordinates       (same space as projected antenna positions)
 *
 * Every stage here is a pure function so it can be unit tested without a camera, a DOM,
 * or a GPU. Nothing else in the app is allowed to do ad-hoc mirroring or aspect maths.
 *
 * All functions accept an optional `out` object and never allocate when one is supplied,
 * because these run on every tracked landmark of every frame.
 */

import type { Point2 } from '../tracking/types';

export type ObjectFit = 'cover' | 'contain';
export type FrameRotation = 0 | 90 | 180 | 270;

/** Everything needed to place a raw camera frame onto the screen exactly as the user sees it. */
export interface VideoDisplayGeometry {
  /** Intrinsic frame size reported by the video track (`videoWidth`/`videoHeight`). */
  frameWidth: number;
  frameHeight: number;
  /** CSS pixel size of the box the video is painted into (also the WebGL canvas size). */
  displayWidth: number;
  displayHeight: number;
  /** How the video fills that box. `cover` matches the app's CSS. */
  objectFit: ObjectFit;
  /** True when the video is painted mirrored (the default for user-facing cameras). */
  mirrored: boolean;
  /**
   * Clockwise rotation applied between the raw frame and what is displayed. Almost always 0
   * because browsers hand back already-oriented frames, but some Android devices expose
   * landscape frames for a portrait viewport.
   */
  rotation: FrameRotation;
}

export interface VideoDisplayTransform {
  /** Frame size after rotation (width/height swap for 90/270). */
  orientedWidth: number;
  orientedHeight: number;
  /** Uniform scale from oriented-frame pixels to display pixels. */
  scale: number;
  /** Display-pixel offset of the oriented frame's top-left corner. Negative when cropped. */
  offsetX: number;
  offsetY: number;
  /** Visible fraction of the oriented frame, per axis (1 for the axis that fills the box). */
  visibleFractionX: number;
  visibleFractionY: number;
  displayWidth: number;
  displayHeight: number;
  /** displayWidth / displayHeight. */
  aspect: number;
  mirrored: boolean;
  rotation: FrameRotation;
}

const EPSILON = 1e-9;

function safeSize(value: number, fallback: number): number {
  return Number.isFinite(value) && value > EPSILON ? value : fallback;
}

/** Frame dimensions after applying `rotation`. */
export function orientedFrameSize(
  frameWidth: number,
  frameHeight: number,
  rotation: FrameRotation,
): { width: number; height: number } {
  return rotation === 90 || rotation === 270
    ? { width: frameHeight, height: frameWidth }
    : { width: frameWidth, height: frameHeight };
}

/**
 * Rotate image-normalized coordinates by `rotation` degrees clockwise into the oriented
 * frame's own normalized space. Both input and output are in [0,1] with origin top-left.
 */
export function rotateNormalized(
  x: number,
  y: number,
  rotation: FrameRotation,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  switch (rotation) {
    case 90:
      out.x = 1 - y;
      out.y = x;
      break;
    case 180:
      out.x = 1 - x;
      out.y = 1 - y;
      break;
    case 270:
      out.x = y;
      out.y = 1 - x;
      break;
    default:
      out.x = x;
      out.y = y;
      break;
  }
  return out;
}

/**
 * Resolve how the oriented frame is laid out inside the display box.
 *
 * `cover` scales up until both axes are filled (cropping the overflow); `contain` scales
 * down until both fit (letterboxing). This mirrors the CSS `object-fit` of the `<video>`.
 */
export function computeVideoDisplayTransform(
  geometry: VideoDisplayGeometry,
): VideoDisplayTransform {
  const frameWidth = safeSize(geometry.frameWidth, 1);
  const frameHeight = safeSize(geometry.frameHeight, 1);
  const displayWidth = safeSize(geometry.displayWidth, 1);
  const displayHeight = safeSize(geometry.displayHeight, 1);

  const oriented = orientedFrameSize(frameWidth, frameHeight, geometry.rotation);
  const scaleX = displayWidth / oriented.width;
  const scaleY = displayHeight / oriented.height;
  const scale =
    geometry.objectFit === 'contain' ? Math.min(scaleX, scaleY) : Math.max(scaleX, scaleY);

  const paintedWidth = oriented.width * scale;
  const paintedHeight = oriented.height * scale;

  return {
    orientedWidth: oriented.width,
    orientedHeight: oriented.height,
    scale,
    offsetX: (displayWidth - paintedWidth) / 2,
    offsetY: (displayHeight - paintedHeight) / 2,
    visibleFractionX: Math.min(1, displayWidth / paintedWidth),
    visibleFractionY: Math.min(1, displayHeight / paintedHeight),
    displayWidth,
    displayHeight,
    aspect: displayWidth / displayHeight,
    mirrored: geometry.mirrored,
    rotation: geometry.rotation,
  };
}

/**
 * image-normalized -> display-normalized.
 *
 * Output is in [0,1] over the visible display box (values can fall outside that range when
 * `object-fit: cover` crops part of the frame — that is intentional and means "off screen").
 * Mirroring is applied last, in display space, which is where the user actually sees it.
 */
export function imageToDisplayNormalized(
  x: number,
  y: number,
  transform: VideoDisplayTransform,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  rotateNormalized(x, y, transform.rotation, out);

  const px = transform.offsetX + out.x * transform.orientedWidth * transform.scale;
  const py = transform.offsetY + out.y * transform.orientedHeight * transform.scale;

  out.x = px / transform.displayWidth;
  out.y = py / transform.displayHeight;

  if (transform.mirrored) out.x = 1 - out.x;
  return out;
}

/**
 * display-normalized -> isotropic screen space.
 * Origin at the centre, +y up, y spans [-0.5, 0.5], x spans [-aspect/2, aspect/2].
 */
export function displayToScreen(
  dx: number,
  dy: number,
  transform: Pick<VideoDisplayTransform, 'aspect'>,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  out.x = (dx - 0.5) * transform.aspect;
  out.y = 0.5 - dy;
  return out;
}

/** isotropic screen space -> display-normalized. Inverse of {@link displayToScreen}. */
export function screenToDisplay(
  sx: number,
  sy: number,
  transform: Pick<VideoDisplayTransform, 'aspect'>,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  out.x = sx / (transform.aspect || 1) + 0.5;
  out.y = 0.5 - sy;
  return out;
}

/** WebGL normalized device coordinates ([-1,1], +y up) -> isotropic screen space. */
export function ndcToScreen(
  ndcX: number,
  ndcY: number,
  aspect: number,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  out.x = ndcX * 0.5 * aspect;
  out.y = ndcY * 0.5;
  return out;
}

/** isotropic screen space -> WebGL normalized device coordinates. */
export function screenToNdc(
  sx: number,
  sy: number,
  aspect: number,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  out.x = aspect > EPSILON ? (sx * 2) / aspect : 0;
  out.y = sy * 2;
  return out;
}

/** display-normalized -> CSS pixels inside the display box. */
export function displayToPixels(
  dx: number,
  dy: number,
  transform: Pick<VideoDisplayTransform, 'displayWidth' | 'displayHeight'>,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  out.x = dx * transform.displayWidth;
  out.y = dy * transform.displayHeight;
  return out;
}

/**
 * Convenience for the full image -> screen chain. Allocation-free when `out` is supplied.
 */
export function imageToScreen(
  x: number,
  y: number,
  transform: VideoDisplayTransform,
  out: Point2 = { x: 0, y: 0 },
): Point2 {
  imageToDisplayNormalized(x, y, transform, out);
  return displayToScreen(out.x, out.y, transform, out);
}

/**
 * Scale MediaPipe's relative `z` into the same units as screen-space x/y.
 *
 * MediaPipe's z is expressed roughly in the same scale as x (i.e. relative to frame width)
 * and is *not* a room-space depth. We keep it only as a weak secondary cue, never as a
 * primary control axis, so a coarse conversion is sufficient and honest.
 */
export function imageDepthToScreenDepth(z: number, transform: VideoDisplayTransform): number {
  const widthInScreenUnits = (transform.orientedWidth * transform.scale) / transform.displayHeight;
  return z * widthInScreenUnits;
}

/** True when a display-normalized point lies inside the visible box (with optional slack). */
export function isWithinDisplay(dx: number, dy: number, slack = 0): boolean {
  return dx >= -slack && dx <= 1 + slack && dy >= -slack && dy <= 1 + slack;
}

/**
 * Stateful helper that caches the transform and exposes allocation-free conversions.
 * One instance is owned by the app and updated whenever the video or layout changes.
 */
export class VideoCoordinateMapper {
  private geometry: VideoDisplayGeometry = {
    frameWidth: 1280,
    frameHeight: 720,
    displayWidth: 1280,
    displayHeight: 720,
    objectFit: 'cover',
    mirrored: false,
    rotation: 0,
  };

  private transformCache: VideoDisplayTransform = computeVideoDisplayTransform(this.geometry);

  private readonly scratch: Point2 = { x: 0, y: 0 };

  /** Returns true when the geometry actually changed (useful to avoid redundant work). */
  update(partial: Partial<VideoDisplayGeometry>): boolean {
    const previous = this.geometry;
    const next: VideoDisplayGeometry = {
      frameWidth: partial.frameWidth ?? previous.frameWidth,
      frameHeight: partial.frameHeight ?? previous.frameHeight,
      displayWidth: partial.displayWidth ?? previous.displayWidth,
      displayHeight: partial.displayHeight ?? previous.displayHeight,
      objectFit: partial.objectFit ?? previous.objectFit,
      mirrored: partial.mirrored ?? previous.mirrored,
      rotation: partial.rotation ?? previous.rotation,
    };
    const changed =
      next.frameWidth !== previous.frameWidth ||
      next.frameHeight !== previous.frameHeight ||
      next.displayWidth !== previous.displayWidth ||
      next.displayHeight !== previous.displayHeight ||
      next.objectFit !== previous.objectFit ||
      next.mirrored !== previous.mirrored ||
      next.rotation !== previous.rotation;
    if (!changed) return false;
    this.geometry = next;
    this.transformCache = computeVideoDisplayTransform(next);
    return true;
  }

  get transform(): VideoDisplayTransform {
    return this.transformCache;
  }

  get aspect(): number {
    return this.transformCache.aspect;
  }

  get mirrored(): boolean {
    return this.geometry.mirrored;
  }

  toDisplay(x: number, y: number, out: Point2 = { x: 0, y: 0 }): Point2 {
    return imageToDisplayNormalized(x, y, this.transformCache, out);
  }

  toScreen(x: number, y: number, out: Point2 = { x: 0, y: 0 }): Point2 {
    return imageToScreen(x, y, this.transformCache, out);
  }

  toScreenDepth(z: number): number {
    return imageDepthToScreenDepth(z, this.transformCache);
  }

  ndcToScreen(ndcX: number, ndcY: number, out: Point2 = { x: 0, y: 0 }): Point2 {
    return ndcToScreen(ndcX, ndcY, this.transformCache.aspect, out);
  }

  screenToNdc(sx: number, sy: number, out: Point2 = { x: 0, y: 0 }): Point2 {
    return screenToNdc(sx, sy, this.transformCache.aspect, out);
  }

  /** display-normalized position of an image-space point, in CSS pixels. */
  toPixels(x: number, y: number, out: Point2 = { x: 0, y: 0 }): Point2 {
    imageToDisplayNormalized(x, y, this.transformCache, this.scratch);
    return displayToPixels(this.scratch.x, this.scratch.y, this.transformCache, out);
  }
}
